import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Check, ArrowLeft, ArrowRight, Sparkles, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Spinner } from "@/components/common";
import { CriteriaBuilder } from "@/features/rubrics/CriteriaBuilder";
import { CandidateUpload } from "@/features/candidates/CandidateUpload";
import { EvaluatorDialog } from "@/features/admin/EvaluatorsPage";
import { cn } from "@/lib/utils";

const STEPS = ["İlan", "Adaylar", "Kategoriler", "Değerlendiriciler", "Gözden Geçir"];

const StepBar = ({ step }) => (
  <ol className="mb-10 flex items-center gap-2" data-testid="wizard-steps">
    {STEPS.map((label, i) => {
      const n = i + 1;
      const state = n < step ? "done" : n === step ? "active" : "todo";
      return (
        <li key={label} className="flex flex-1 items-center gap-2">
          <div className="flex items-center gap-2">
            <span className={cn("flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold transition-colors duration-200", state === "done" && "bg-plena-success text-white", state === "active" && "bg-plena-text text-white", state === "todo" && "bg-plena-surface2 text-plena-text2")}>{state === "done" ? <Check className="h-3.5 w-3.5" /> : n}</span>
            <span className={cn("hidden text-sm sm:inline", state === "active" ? "font-semibold" : "text-plena-text2")}>{label}</span>
          </div>
          {i < STEPS.length - 1 && <div className={cn("h-px flex-1", n < step ? "bg-plena-success" : "bg-plena-border")} />}
        </li>
      );
    })}
  </ol>
);

const Field = ({ label, htmlFor, optional, children }) => (
  <div className="space-y-1.5"><Label htmlFor={htmlFor}>{label}{optional && <span className="ml-1 text-plena-muted">(isteğe bağlı)</span>}</Label>{children}</div>
);

function StepJob({ job, onNext }) {
  const [form, setForm] = useState({ title: "", department: "", description: "", code: "", location: "", notes: "", ...(job || {}) });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => { e.preventDefault(); setBusy(true); try { await onNext(form); } finally { setBusy(false); } };
  return (
    <form onSubmit={submit} className="space-y-6">
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Pozisyon" htmlFor="job-title"><Input id="job-title" data-testid="job-title-input" value={form.title} onChange={set("title")} required className="h-11" /></Field>
        <Field label="Departman" htmlFor="job-dept" optional><Input id="job-dept" data-testid="job-department-input" value={form.department || ""} onChange={set("department")} className="h-11" /></Field>
      </div>
      <Field label="İlan Metni" htmlFor="job-desc">
        <Textarea id="job-desc" data-testid="job-description-input" value={form.description} onChange={set("description")} required rows={16} className="resize-y rounded-xl text-[15px] leading-7" placeholder="Pozisyonun amacı, sorumluluklar ve aranan nitelikler…" />
      </Field>
      <div className="grid gap-5 sm:grid-cols-3">
        <Field label="İlan Kodu" htmlFor="job-code" optional><Input id="job-code" data-testid="job-code-input" value={form.code || ""} onChange={set("code")} /></Field>
        <Field label="Lokasyon" htmlFor="job-loc" optional><Input id="job-loc" data-testid="job-location-input" value={form.location || ""} onChange={set("location")} /></Field>
        <Field label="Notlar" htmlFor="job-notes" optional><Input id="job-notes" data-testid="job-notes-input" value={form.notes || ""} onChange={set("notes")} /></Field>
      </div>
      <div className="flex justify-end"><Button type="submit" data-testid="wizard-next-button" disabled={busy} className="btn-press rounded-full">Devam <ArrowRight className="ml-1.5 h-4 w-4" /></Button></div>
    </form>
  );
}

function StepEvaluators({ selected, onChange }) {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => api.get("/admin/evaluators").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);
  if (!items) return <Spinner />;
  const toggle = (id) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-plena-text2">{selected.length} değerlendirici seçildi. Her değerlendiriciye benzersiz ve güvenli bir bağlantı oluşturulacak.</p>
        <Button variant="outline" data-testid="wizard-add-evaluator-button" onClick={() => setOpen(true)} className="rounded-full"><Plus className="mr-1.5 h-4 w-4" /> Yeni Değerlendirici</Button>
      </div>
      <div className="plena-card divide-y divide-plena-border">
        {items.length === 0 && <p className="px-6 py-8 text-sm text-plena-text2">Henüz değerlendirici yok.</p>}
        {items.map((ev) => (
          <label key={ev.id} data-testid={`select-evaluator-${ev.id}`} className="flex cursor-pointer items-center gap-4 px-5 py-4 hover:bg-plena-surface2/50">
            <Checkbox checked={selected.includes(ev.id)} onCheckedChange={() => toggle(ev.id)} data-testid={`evaluator-checkbox-${ev.id}`} />
            <div className="min-w-0 flex-1"><p className="font-medium">{ev.name}</p><p className="truncate text-sm text-plena-text2">{ev.email}{ev.title ? ` · ${ev.title}` : ""}</p></div>
          </label>
        ))}
      </div>
      <EvaluatorDialog open={open} onOpenChange={setOpen} initial={null} onSaved={(ev) => { load(); onChange([...selected, ev.id]); }} />
    </div>
  );
}

const Summary = ({ label, value, testId }) => (
  <div className="plena-card px-6 py-5" data-testid={testId}><p className="text-xs font-medium text-plena-text2">{label}</p><p className="mt-1 font-heading text-2xl font-bold">{value}</p></div>
);

export default function CreateEvaluationWizard() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const step = Math.min(5, Math.max(1, Number(params.get("step")) || 1));
  const [study, setStudy] = useState(null);
  const [evaluators, setEvaluators] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!id) return;
    api.get(`/admin/studies/${id}`).then((r) => { if (r.data.status !== "DRAFT") nav(`/admin/evaluations/${id}`, { replace: true }); setStudy(r.data); });
    api.get("/admin/evaluators").then((r) => setEvaluators(r.data));
  }, [id, nav]);

  const go = (s) => setParams({ step: String(s) });

  const saveJob = async (form) => {
    const payload = { ...form, department: form.department || null, code: form.code || null, location: form.location || null, notes: form.notes || null };
    try {
      if (!id) { const r = await api.post("/admin/studies", { job: payload }); nav(`/admin/evaluations/${r.data.id}/setup?step=2`); }
      else { const r = await api.put(`/admin/studies/${id}/job`, payload); setStudy({ ...study, job: r.data, title: r.data.title }); go(2); }
    } catch (e) { toast.error(errText(e)); }
  };

  const saveRubric = async (rubric) => {
    setStudy({ ...study, rubric });
    try { await api.put(`/admin/studies/${id}/rubric`, rubric); } catch (e) { toast.error(errText(e)); }
  };
  const applyStandard = async () => {
    try { const r = await api.post(`/admin/studies/${id}/criteria/suggest`); saveRubric({ categories: r.data.categories, criteria: r.data.criteria }); toast.success("Varsayılan kategori ayarları uygulandı."); } catch (e) { toast.error(errText(e)); }
  };
  const saveEvaluators = async (ids) => { setStudy({ ...study, selected_evaluator_ids: ids }); await api.put(`/admin/studies/${id}/evaluators`, { evaluator_ids: ids }).catch((e) => toast.error(errText(e))); };
  const launch = async () => {
    setBusy(true);
    try { await api.post(`/admin/studies/${id}/launch`); toast.success("Değerlendirme başlatıldı"); nav(`/admin/evaluations/${id}?tab=evaluators`); } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  if (id && !study) return <Spinner />;
  const criteriaCount = study ? study.rubric.criteria.filter((c) => c.is_active !== false).length : 0;
  const problems = study ? [
    !study.candidates.length && "En az bir aday yükleyin",
    !criteriaCount && "En az bir kategori gerekli",
    !study.selected_evaluator_ids.length && "En az bir değerlendirici seçin",
  ].filter(Boolean) : [];

  return (
    <div className="fade-up mx-auto max-w-4xl">
      <div className="mb-8">
        <p className="eyebrow mb-2">Yeni Değerlendirme</p>
        <h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">{study?.title || "Değerlendirme oluştur"}</h1>
      </div>
      <StepBar step={id ? step : 1} />

      {(!id || step === 1) && <StepJob job={study?.job} onNext={saveJob} />}

      {id && step === 2 && (
        <div className="space-y-6">
          <CandidateUpload studyId={id} candidates={study.candidates} onChange={(c) => setStudy({ ...study, candidates: c })} />
          <Nav onBack={() => go(1)} onNext={() => go(3)} nextDisabled={!study.candidates.length} />
        </div>
      )}

      {id && step === 3 && (
        <div className="space-y-6">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-plena-text2">Kategoriler sabittir; her kategorinin açıklamasını bu pozisyona göre düzenleyebilirsiniz. Açıklama değerlendiriciye gösterilir.</p>
            <Button variant="outline" data-testid="apply-standard-criteria-button" onClick={applyStandard} className="shrink-0 rounded-full"><Sparkles className="mr-1.5 h-4 w-4" /> Varsayılanlara sıfırla</Button>
          </div>
          <CriteriaBuilder rubric={study.rubric} onChange={saveRubric} />
          <Nav onBack={() => go(2)} onNext={() => go(4)} nextDisabled={!criteriaCount} />
        </div>
      )}

      {id && step === 4 && (
        <div className="space-y-6">
          <StepEvaluators selected={study.selected_evaluator_ids} onChange={saveEvaluators} />
          <Nav onBack={() => go(3)} onNext={() => go(5)} nextDisabled={!study.selected_evaluator_ids.length} />
        </div>
      )}

      {id && step === 5 && (
        <div className="space-y-8">
          <div className="plena-card px-6 py-5">
            <p className="eyebrow mb-1">İlan</p>
            <p className="font-heading text-lg font-semibold">{study.job.title}</p>
            <p className="text-sm text-plena-text2">{[study.job.department, study.job.location].filter(Boolean).join(" · ")}</p>
            <p className="mt-3 line-clamp-4 whitespace-pre-line text-sm text-plena-text2">{study.job.description}</p>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <Summary label="Aday" value={study.candidates.length} testId="review-candidate-count" />
            <Summary label="Değerlendirme Kategorisi" value={criteriaCount} testId="review-criteria-count" />
            <Summary label="Değerlendirici" value={study.selected_evaluator_ids.length} testId="review-evaluator-count" />
          </div>
          <div className="plena-card px-6 py-5">
            <p className="eyebrow mb-3">Değerlendiriciler</p>
            <ul className="space-y-1.5 text-sm">{evaluators.filter((e) => study.selected_evaluator_ids.includes(e.id)).map((e) => <li key={e.id}>{e.name} <span className="text-plena-text2">· {e.email}</span></li>)}</ul>
          </div>
          {problems.length > 0 && <ul data-testid="review-problems" className="space-y-1 rounded-xl bg-[#FDF4DC] px-5 py-4 text-sm text-[#8A6414]">{problems.map((p) => <li key={p}>• {p}</li>)}</ul>}
          <p className="text-sm text-plena-text2">Başlatıldığında kategori açıklamaları ve ilan metni bu sürümde dondurulur; her değerlendirici için adaylar rastgele sırayla sunulur.</p>
          <div className="flex items-center justify-between">
            <Button variant="ghost" onClick={() => go(4)} className="rounded-full"><ArrowLeft className="mr-1.5 h-4 w-4" /> Geri</Button>
            <Button data-testid="launch-button" onClick={launch} disabled={busy || problems.length > 0} className="btn-press h-11 rounded-full px-6 font-semibold">Değerlendirmeyi Başlat</Button>
          </div>
        </div>
      )}
    </div>
  );
}

const Nav = ({ onBack, onNext, nextDisabled }) => (
  <div className="flex items-center justify-between pt-2">
    <Button variant="ghost" data-testid="wizard-back-button" onClick={onBack} className="rounded-full"><ArrowLeft className="mr-1.5 h-4 w-4" /> Geri</Button>
    <Button data-testid="wizard-next-button" onClick={onNext} disabled={nextDisabled} className="btn-press rounded-full">Devam <ArrowRight className="ml-1.5 h-4 w-4" /></Button>
  </div>
);
